import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react'
import './Button.css'

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost'
export type ButtonSize = 'small' | 'medium' | 'large'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button visual style */
  variant?: ButtonVariant
  /** Button size */
  size?: ButtonSize
  /** Loading state - shows spinner and disables button */
  loading?: boolean
  /** Full width button */
  fullWidth?: boolean
  /** Icon to show before text */
  icon?: ReactNode
  /** Children elements */
  children: ReactNode
}

/**
 * Primary button component following Derek-friendly design:
 * - Minimum 48px touch target
 * - High contrast colors
 * - Clear disabled states
 * - Loading state with spinner
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'medium',
      loading = false,
      fullWidth = false,
      icon,
      disabled,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    const buttonClasses = [
      'ps-btn',
      `ps-btn--${variant}`,
      `ps-btn--${size}`,
      fullWidth && 'ps-btn--full-width',
      loading && 'ps-btn--loading',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <button
        ref={ref}
        className={buttonClasses}
        disabled={isDisabled}
        aria-busy={loading}
        aria-disabled={isDisabled}
        {...props}
      >
        {loading && (
          <span className="ps-btn__spinner" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="31.416"
                strokeDashoffset="10"
              />
            </svg>
          </span>
        )}
        {!loading && icon && <span className="ps-btn__icon">{icon}</span>}
        <span className="ps-btn__text">{children}</span>
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button
